import { UseTranslate } from '../hooks/Translate'
import React, { FC, useEffect, useRef, useState } from 'react'
import styles from './GoogleMaps.module.scss'

interface GoogleMapsProps {
  zoom: number
  class: string
  geoCodeByPinLocation: boolean
  location?: number[]
  autoZoom: boolean
  showMarker?: boolean
  onPrediction?: (prediction: Record<string, any>) => void
  centerPosition: {
    lat: number
    lng: number
  }
  zoomControl?: boolean
  mapTypeControl?: boolean
}

const GoogleMaps: FC<
  React.PropsWithChildren<
    React.PropsWithChildren<React.PropsWithChildren<GoogleMapsProps>>
  >
> = (props: GoogleMapsProps) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<any>()
  const [marker, setMarker] = useState<any>()
  const [isLoaded, setIsLoaded] = useState(false)
  const [translate] = UseTranslate()

  // Check if Google Maps API is loaded
  const checkGoogleMapsLoaded = () => {
    return typeof window !== 'undefined' && window.google && window.google.maps
  }

  const {
    showMarker = false,
    zoomControl = true,
    mapTypeControl = true,
  } = props

  useEffect(() => {
    if (!checkGoogleMapsLoaded()) {
      console.warn('Google Maps API not loaded yet')
      return
    }

    if (!mapRef.current) {
      console.warn('Map container not available yet')
      return
    }

    const maps = window.google.maps
    const map = new maps.Map(mapRef.current, {
      zoomControl: zoomControl && !isMobile(),
      mapTypeControl: mapTypeControl && !isMobile(),
      mapTypeControlOptions: {
        style: maps.MapTypeControlStyle.DROPDOWN_MENU,
        mapTypeIds: ['satellite', 'roadmap'],
      },
      streetViewControl: false,
      keyboardShortcuts: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: maps.ControlPosition.TOP_LEFT,
      },
      center: props.centerPosition,
      zoom: props.zoom,
    })

    if (showMarker) {
      const marker = new maps.Marker({
        position: props.centerPosition,
        draggable: false,
        map: map,
      })

      // Create custom overlay class
      class HTMLMarker extends (maps as any).OverlayView {
        private button: HTMLButtonElement | null = null;

        onAdd() {
          const button = document.createElement('button')
          button.type = 'button'
          button.className = styles.selectAddress
          button.innerText = translate('selectAddress')
          this.button = button
          ;(this as any).getPanes().overlayImage.appendChild(button)
          
          button.onclick = () => {
            if (props.onPrediction) {
              const geocoder = new maps.Geocoder()
              const location = map.getCenter()
              geocoder.geocode({ location }, (result: any[], status: any) => {
                if (result && result.length) {
                  const prediction = result[0]
                  const formatterPrediction = {
                    title: prediction.formatted_address,
                    subTitle: prediction.address_components[2]?.long_name || '',
                    placeId: prediction.place_id,
                    geolocation: {
                      type: "Point",
                      coordinates: [location.lat(), location.lng()],
                    },
                  }
                  props.onPrediction!(formatterPrediction)
                }
              })
            }
          }
        }

        draw() {
          const overlayImage = (this as any).getPanes().overlayImage as HTMLElement
          const position = (this as any).getProjection().fromLatLngToDivPixel(
            map.getCenter(),
          )
          if (overlayImage && position) {
            overlayImage.style.left = position.x + 'px'
            overlayImage.style.top = position.y - 45 + 'px'
            overlayImage.style.transform = 'translate(-50%, -100%)'
            overlayImage.style.width = 'auto'
          }
        }

        onRemove() {
          if (this.button && this.button.parentNode) {
            this.button.parentNode.removeChild(this.button)
          }
        }
      }

      const htmlMarker = new HTMLMarker()
      ;(htmlMarker as any).setMap(map)

      map.addListener('drag', () => {
        marker.setPosition(map.getCenter())
        const overlayImage = (htmlMarker as any).getPanes()?.overlayImage as HTMLElement
        if (overlayImage) {
          overlayImage.style.display = 'none'
        }
      })

      map.addListener('dragend', () => {
        window.setTimeout(() => {
          marker.setPosition(map.getCenter())
          const overlayImage = (htmlMarker as any).getPanes()?.overlayImage as HTMLElement
          if (overlayImage) {
            overlayImage.style.display = 'block'
          }
        }, 200)
      })
      setMarker(marker)
    }
    setMap(map)
    map.setZoom(16)
    setIsLoaded(true)
  }, [props.centerPosition])

  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  }

  useEffect(() => {
    if (props.location) {
      const [lat, lng] = props.location
      map?.setCenter({ lat, lng })
      marker?.setPosition({ lat, lng })
      if (props.autoZoom) map?.setZoom(16)
    }
  }, [props.location])

  return <div className={props.class} ref={mapRef}></div>
}

export default GoogleMaps 